'use client';

import { useEffect, useRef } from 'react';

import { GA4_MEASUREMENT_ID, GOOGLE_ADS_ID } from '@m/config/env';
import { usePathname } from 'next/navigation';
import Script from 'next/script';

import { gtagPageView } from '@m/utils/gtag';

/**
 * Loads the Google tag (gtag.js) for GA4 and Google Ads, and owns `page_view`
 * for client-side navigation.
 *
 * Loading and page-view tracking are deliberately in one component: the config
 * below sets `send_page_view: false`, so mounting the loader without the
 * tracker would silently drop every page view.
 *
 * Mount this per-app rather than from `InitApp` — that component is shared with
 * displays/kiosk/operator/performance, which should not be tagged.
 *
 * Keep in sync with the `Google tag` block in repo-root `public/index.html`,
 * which covers the CRA customer app (CRA HTML cannot import TypeScript).
 */
export default function GtagInit() {
  const pathname = usePathname();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    if (!GA4_MEASUREMENT_ID || lastTrackedPath.current === pathname) {
      return;
    }
    lastTrackedPath.current = pathname;
    gtagPageView({
      page_location: window.location.href,
      page_path: pathname,
      page_title: document.title,
    });
  }, [pathname]);

  if (!GA4_MEASUREMENT_ID) {
    return null;
  }

  return (
    <>
      <Script
        id="google-tag-loader"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`}
      />
      <Script
        id="google-tag-config"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${GA4_MEASUREMENT_ID}', { send_page_view: false });
${GOOGLE_ADS_ID ? `gtag('config', '${GOOGLE_ADS_ID}');` : ''}
`,
        }}
      />
    </>
  );
}
